// React
import { useState, useCallback, useEffect } from 'react';

// UI primitives
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Field, FieldLabel, FieldContent, FieldTitle } from '@/components/ui/field';
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from '@/components/ui/tooltip';

// Icons
import { InfoIcon } from '@/react/components/icons/InfoIcon';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useCreateDiscount } from '@hooks/useCreateDiscount';
import { useCreateSurcharge } from '@hooks/useCreateSurcharge';
import { useTranslation } from '@hooks/useTranslation';
import { ApiError } from '@/react/api/client';
import { useValidationNavigation } from '@components/Form/useValidationNavigation';
import type { CalculationType } from '@/react/api/calculationType';

// Local
import { MultiplierDropdown } from './MultiplierDropdown';
import { FIELD_SURFACE } from "@/components/ui/field-surface";
import {
  type ModifierCategory,
  type ModifierType,
} from '@components/EditEvent/EventPriceModifiersSection/constants';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface CreatedPriceModifier {
  modifier_type: ModifierType;
  modifier_id: number;
  name: string;
  amount: string;
  category: Extract<ModifierCategory, 'discount' | 'surcharge'>;
  calculation_type: CalculationType;
}

export type CreateAdjustmentCategory = Extract<ModifierCategory, 'discount' | 'surcharge'>;

type FieldErrors = Partial<Record<'name' | 'amount' | 'calculation_type' | 'max_value' | '_base', string>>;

interface CreatePriceModifierDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (modifier: CreatedPriceModifier) => void;
  initialCategory?: CreateAdjustmentCategory;
  /**
   * When true, the Adjustment type radios are disabled — the caller has fixed
   * the category (e.g. opening from the Discounts tab on the price modifiers
   * list page). Pair with `initialCategory` to set the locked value.
   */
  lockCategory?: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const LABEL_CLASS = 'font-inter text-sm font-semibold text-grey';
const FIELD_GAP = 'gap-3'; // 12px between label and input
const INPUT_CLASS = cn(
  cn(FIELD_SURFACE, 'h-[42px] px-[18px] py-3 font-inter text-sm text-off-black'),
  'placeholder:text-grey-light focus:[&]:border-brand-yellow focus:[&[type]]:border-brand-yellow focus:[&]:ring-0 focus:[&[type]]:ring-0 focus:[&]:outline-none focus:[&[type]]:outline-none',
);
const DROPDOWN_TRIGGER_OVERRIDE = 'h-[42px] px-[18px] py-3';
const TOOLTIP_DELAY_MS = 200;

function FieldErrorText({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" data-validation-error="true" className="mt-1 font-inter text-xs text-chilli-red">
      {message}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*  CreatePriceModifierDialog                                                  */
/* -------------------------------------------------------------------------- */

export function CreatePriceModifierDialog({
  isOpen,
  onOpenChange,
  onSuccess,
  initialCategory,
  lockCategory = false,
}: CreatePriceModifierDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ModifierCategory | ''>(initialCategory ?? '');
  const [amount, setAmount] = useState('');
  const [multiplierType, setMultiplierType] = useState<CalculationType | ''>('');
  const [maxValue, setMaxValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { elementRef, requestNavigation } = useValidationNavigation();

  const createDiscount = useCreateDiscount();
  const createSurcharge = useCreateSurcharge();
  const isPending = createDiscount.isPending || createSurcharge.isPending;

  // Reset form on each open so a fresh entry context (e.g. switching from the
  // Discount filter to the Surcharge filter) re-seeds the pre-selected category.
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setCategory(initialCategory ?? '');
    setAmount('');
    setMultiplierType('');
    setMaxValue('');
    setFieldErrors({});
  }, [isOpen, initialCategory]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // Portalled inside the edit event form; don't submit that form too.
      e.stopPropagation();
      // Mirror `isValid` — Enter-key submission in a text input bypasses the
      // disabled submit button, so the guard has to cover every required field.
      if (category === '' || multiplierType === '' || isPending) return;
      setFieldErrors({});

      const payload = {
        name,
        amount,
        calculation_type: multiplierType,
      };

      // A cap only makes sense for a percentage discount — a flat or per-pax
      // amount is already a fixed dollar figure.
      const trimmedMaxValue = maxValue.trim();
      const maxValuePayload =
        multiplierType === 'percentage' && trimmedMaxValue !== '' ? trimmedMaxValue : null;

      try {
        if (category === 'discount') {
          const { discount } = await createDiscount.mutateAsync({...payload, max_value: maxValuePayload});
          onSuccess({
            modifier_type: 'Discount',
            modifier_id: discount.id,
            name: discount.name,
            amount: discount.amount,
            category: 'discount',
            calculation_type: discount.calculation_type,
          });
        } else {
          const { surcharge } = await createSurcharge.mutateAsync(payload);
          onSuccess({
            modifier_type: 'Surcharge',
            modifier_id: surcharge.id,
            name: surcharge.name,
            amount: surcharge.amount,
            category: 'surcharge',
            calculation_type: surcharge.calculation_type,
          });
        }
        onOpenChange(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 422 && err.fieldErrors) {
          setFieldErrors(err.fieldErrors);
          requestNavigation();
        } else if (err instanceof ApiError) {
          setFieldErrors({_base: err.message});
        } else {
          throw err;
        }
      }
    },
    [name, category, amount, multiplierType, maxValue, createDiscount, createSurcharge, isPending, onSuccess, onOpenChange, requestNavigation],
  );

  const isPercentage = multiplierType === 'percentage';

  const isValid =
    name.trim().length > 0 &&
    category !== '' &&
    amount.trim().length > 0 &&
    multiplierType !== '';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={handleClose}
        className={cn(
          'max-w-dialog rounded-input border-dinnerplate-white px-0 pb-0 bg-body outline-none overflow-hidden',
          '[&>button:last-child]:hidden',
        )}
      >
        <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
          <ModalLayout
            as="form"
            ref={elementRef}
            onSubmit={handleSubmit}
            title={
              <DialogTitle asChild>
                <ModalTitle>Create new price modifier</ModalTitle>
              </DialogTitle>
            }
            onClose={handleClose}
            footer={
              <div className="flex w-full items-center justify-between gap-4">
                {fieldErrors._base ? (
                  <p
                    role="alert"
                    className="font-inter text-xs text-chilli-red"
                  >
                    {fieldErrors._base}
                  </p>
                ) : (
                  <span aria-hidden="true" />
                )}
                <Button type="submit" disabled={!isValid || isPending}>
                  {t('create_modal.create')}
                </Button>
              </div>
            }
          >
            <div className="flex flex-col gap-6">
              {/* Row 1: Modifier name + Modifier type */}
              <div className="grid grid-cols-2 gap-6">
                <Field className={FIELD_GAP}>
                  <FieldTitle className={LABEL_CLASS}>
                    Name
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          aria-label="More info"
                          className="p-0 text-grey hover:bg-transparent"
                        >
                          <InfoIcon />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent align="center">
                        {t('common.tooltips.name', 'This name appears on customer-facing documents.')}
                      </TooltipContent>
                    </Tooltip>
                  </FieldTitle>
                  <FieldContent>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="E.g. delivery fee waiver…"
                      aria-invalid={Boolean(fieldErrors.name)}
                      className={INPUT_CLASS}
                    />
                    <FieldErrorText message={fieldErrors.name} />
                  </FieldContent>
                </Field>

                <Field className={FIELD_GAP}>
                  <FieldLabel className={LABEL_CLASS}>Adjustment type</FieldLabel>
                  <FieldContent>
                    <RadioGroup
                      value={category || undefined}
                      onValueChange={(val) => setCategory(val as ModifierCategory)}
                      disabled={lockCategory}
                      className="flex gap-8 pt-3"
                    >
                      <label className={cn("flex items-center gap-2 font-inter text-sm", lockCategory ? 'cursor-not-allowed' : 'cursor-pointer', category === 'discount' ? 'text-off-black' : 'text-grey')}>
                        <RadioGroupItem value="discount" />
                        Discount
                      </label>
                      <label className={cn("flex items-center gap-2 font-inter text-sm", lockCategory ? 'cursor-not-allowed' : 'cursor-pointer', category === 'surcharge' ? 'text-off-black' : 'text-grey')}>
                        <RadioGroupItem value="surcharge" />
                        Surcharge
                      </label>
                    </RadioGroup>
                  </FieldContent>
                </Field>
              </div>

              {/* Row 2: Multiplier + Amount (mirrors edit page layout) */}
              <div className="grid grid-cols-2 gap-6">
                <Field className={FIELD_GAP}>
                  <FieldLabel className={LABEL_CLASS}>Multiplier</FieldLabel>
                  <FieldContent>
                    <MultiplierDropdown
                      value={multiplierType}
                      onChange={setMultiplierType}
                      triggerClassName={DROPDOWN_TRIGGER_OVERRIDE}
                    />
                    <FieldErrorText message={fieldErrors.calculation_type} />
                  </FieldContent>
                </Field>

                <Field className={FIELD_GAP}>
                  <FieldLabel className={LABEL_CLASS}>Amount</FieldLabel>
                  <FieldContent>
                    <div className="relative">
                      {!isPercentage && (
                        <span className="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 font-inter text-sm text-grey-light">
                          $
                        </span>
                      )}
                      <Input
                        type="text"
                        inputMode="decimal"
                        spellCheck={false}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        aria-label="Amount"
                        aria-invalid={Boolean(fieldErrors.amount)}
                        className={cn(INPUT_CLASS, isPercentage ? 'pr-[30px]' : 'pl-[30px]')}
                      />
                      {isPercentage && (
                        <span className="pointer-events-none absolute right-[18px] top-1/2 -translate-y-1/2 font-inter text-sm text-grey-light">
                          %
                        </span>
                      )}
                    </div>
                    <FieldErrorText message={fieldErrors.amount} />
                  </FieldContent>
                </Field>
              </div>

              {/* Row 3: Max discount — percentage discounts only */}
              {category === 'discount' && isPercentage && (
                <div className="grid grid-cols-2 gap-6">
                  <Field className={FIELD_GAP}>
                    <FieldLabel className={LABEL_CLASS}>Max discount ($)</FieldLabel>
                    <FieldContent>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 font-inter text-sm text-grey-light">
                          $
                        </span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          spellCheck={false}
                          value={maxValue}
                          onChange={(e) => setMaxValue(e.target.value)}
                          placeholder="No cap"
                          aria-label="Max discount ($)"
                          aria-invalid={Boolean(fieldErrors.max_value)}
                          className={cn(INPUT_CLASS, 'pl-[30px]')}
                        />
                      </div>
                      <FieldErrorText message={fieldErrors.max_value} />
                    </FieldContent>
                  </Field>
                </div>
              )}
            </div>
          </ModalLayout>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
